import React, { useState } from 'react';
import { Send, Mail, Smartphone, CheckCircle } from 'lucide-react';

const SendInvitation = ({ onClose, onSend }) => {
  const [invitationType, setInvitationType] = useState('email');
  const [contactValue, setContactValue]     = useState('');
  const [error, setError]                   = useState('');
  const [isSending, setIsSending]           = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const isPhone = invitationType === 'sms';

  const handleInvitationTypeChange = (type) => {
    setInvitationType(type);
    setContactValue('');
    setError('');
    setSuccessMessage('');
  };

  const validateInput = () => {
    if (!contactValue.trim()) {
      setError(isPhone ? 'Phone number is required' : 'Email address is required');
      return false;
    }

    if (!isPhone) {
      if (!/\S+@\S+\.\S+/.test(contactValue)) {
        setError('Please enter a valid email address');
        return false;
      }
    } else {
      // Accept UK local (07xxx), international (+44 / 0044) and plain digit strings
      if (!/^[\d\s+\-()]+$/.test(contactValue)) {
        setError('Please enter a valid phone number');
        return false;
      }
      if (contactValue.replace(/\D/g, '').length < 10) {
        setError('Phone number is too short');
        return false;
      }
    }
    return true;
  };

  const handleSend = async () => {
    if (!validateInput()) return;

    setIsSending(true);
    setError('');
    setSuccessMessage('');

    try {
      await onSend({
        type:      invitationType,
        contact:   contactValue,
        timestamp: new Date().toISOString()
      });

      setSuccessMessage(
        isPhone
          ? `Text message sent to ${contactValue}`
          : `Invitation email sent to ${contactValue}`
      );
      setIsSending(false);

      setTimeout(() => { onClose(); }, 3000);

    } catch (err) {
      setIsSending(false);
      console.error('SendInvitation error:', err);

      const errorMessage = err.message || err.error || 'Unknown error occurred';

      if (
        errorMessage.toLowerCase().includes('already been sent') ||
        errorMessage.toLowerCase().includes('already exists')   ||
        errorMessage.toLowerCase().includes('invalid')          ||
        errorMessage.toLowerCase().includes('not found')        ||
        errorMessage.toLowerCase().includes('required')
      ) {
        setError(errorMessage);
      }
    }
  };

  // ── Method option card ──────────────────────────────────────────────────────
  const MethodCard = ({ value, icon: Icon, label, activeColor, activeBg }) => (
    <label style={{
      display:     'flex',
      alignItems:  'center',
      gap:         '0.75rem',
      padding:     '0.875rem 1rem',
      border:      '2px solid',
      borderColor: invitationType === value ? activeColor : '#e5e7eb',
      borderRadius:'0.5rem',
      cursor:      'pointer',
      background:  invitationType === value ? activeBg : 'white',
      transition:  'all 0.2s'
    }}>
      <input
        type="radio"
        name="invitationType"
        value={value}
        checked={invitationType === value}
        onChange={(e) => handleInvitationTypeChange(e.target.value)}
        style={{ width: '1.125rem', height: '1.125rem', cursor: 'pointer', accentColor: activeColor }}
      />
      <Icon size={20} color={activeColor} />
      <span style={{ fontSize: '0.9375rem', fontWeight: '500', color: '#111827' }}>
        {label}
      </span>
    </label>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="template-modal-overlay">
      <div className="template-modal">
        <div className="modal-header">
          <h3>Send Invitation</h3>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={isSending || !!successMessage}
          >
            ×
          </button>
        </div>

        <div className="template-list">

          {/* ── Success ── */}
          {successMessage && (
            <div style={{
              padding:         '1rem',
              backgroundColor: '#d1fae5',
              border:          '1px solid #10b981',
              borderRadius:    '0.5rem',
              marginBottom:    '1rem',
              display:         'flex',
              alignItems:      'center',
              gap:             '0.75rem'
            }}>
              <CheckCircle size={24} color="#10b981" />
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, color: '#065f46', fontWeight: '500', fontSize: '0.9375rem' }}>
                  {successMessage}
                </p>
                <p style={{ margin: '0.25rem 0 0 0', color: '#059669', fontSize: '0.8125rem' }}>
                  This window will close automatically…
                </p>
              </div>
            </div>
          )}

          {/* ── Form ── */}
          {!successMessage && (
            <>
              {/* Method selection */}
              <div className="form-group">
                <label className="form-label">Select Invitation Method</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <MethodCard
                    value="email"
                    icon={Mail}
                    label="Email"
                    activeColor="#2563eb"
                    activeBg="#eff6ff"
                  />
                  <MethodCard
                    value="sms"
                    icon={Smartphone}
                    label="Text Message (SMS)"
                    activeColor="#7c3aed"
                    activeBg="#f5f3ff"
                  />
                </div>
              </div>

              {/* Contact input */}
              <div className="form-group">
                <label className="form-label">
                  {isPhone ? 'Phone Number' : 'Email Address'} *
                </label>
                <input
                  type={isPhone ? 'tel' : 'email'}
                  value={contactValue}
                  onChange={(e) => { setContactValue(e.target.value); setError(''); }}
                  placeholder={isPhone ? '+44 7700 900000' : 'john@example.com'}
                  disabled={isSending}
                  className={`form-input ${error ? 'error' : ''}`}
                />
                {isPhone && (
                  <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.78rem', color: '#6b7280' }}>
                    Include country code, e.g. +44 for UK
                  </span>
                )}
                {error && (
                  <span className="error-text" style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.875rem', color: '#ef4444' }}>
                    {error}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="form-actions">
                <button type="button" onClick={onClose} disabled={isSending} className="btn-cancel">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending}
                  className="btn-save"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Send size={18} />
                  {isSending ? 'Sending…' : 'Send Invitation'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendInvitation;
